export const diagnosticMetrics = ['bitrateKbps', 'decodeFps', 'packetsReceived', 'packetsLost', 'framesDecoded', 'framesDropped', 'freezeCount', 'freezeDurationMs', 'jitterMs', 'rttMs', 'decodeMs', 'jitterBufferMs', 'width', 'height', 'nackCount', 'pliCount', 'firCount', 'iceErrorCode'] as const;
export type NativeVideoDiagnostics = { sequence: number; sampledAtMs: number; state: string; metrics: Partial<Record<typeof diagnosticMetrics[number], number>> };
export function validDiagnostics(value: unknown): value is NativeVideoDiagnostics {
  const d = value as NativeVideoDiagnostics;
  return !!d && Object.keys(d).length === 4 && Number.isSafeInteger(d.sequence) && d.sequence > 0 && Number.isFinite(d.sampledAtMs) && d.sampledAtMs >= 0 &&
    ['new', 'connecting', 'connected', 'disconnected', 'failed', 'closed'].includes(d.state) && !!d.metrics && typeof d.metrics === 'object' && !Array.isArray(d.metrics) &&
    Object.entries(d.metrics).every(([key, n]) => diagnosticMetrics.includes(key as typeof diagnosticMetrics[number]) && typeof n === 'number' && Number.isFinite(n) && n >= (key === 'packetsLost' ? -1e12 : 0) && n <= 1e15);
}
/** One bounded snapshot per second; missing browser measurements remain absent. */
export class NativeVideoDiagnosticsCollector {
  private previous?: Record<string, any>;
  private lastSample = -Infinity;
  private sequence = 0;
  sample(report: RTCStatsReport, state: RTCPeerConnectionState, now: number, iceErrorCode?: number, monotonicMs = now): NativeVideoDiagnostics | undefined {
    if (monotonicMs - this.lastSample < 1000) return;
    this.lastSample = monotonicMs;
    let video: any, pair: any;
    const entries = new Map<string, any>();
    const transports: any[] = [];
    report.forEach((s) => {
      entries.set(s.id, s);
      if (s.type === 'inbound-rtp' && s.kind === 'video') video = s;
      if (s.type === 'transport') transports.push(s);
    });
    // Follow the video transport, never an unrelated audio/data transport.
    const transport = video?.transportId ? entries.get(video.transportId) : transports.length === 1 ? transports[0] : undefined;
    if (transport?.selectedCandidatePairId) pair = entries.get(transport.selectedCandidatePairId);
    const metrics: NativeVideoDiagnostics['metrics'] = {};
    const put = (key: typeof diagnosticMetrics[number], n: unknown) => {
      if (typeof n === 'number' && Number.isFinite(n) && n >= (key === 'packetsLost' ? -1e12 : 0) && n <= 1e15) metrics[key] = n;
    };
    for (const key of ['packetsReceived', 'packetsLost', 'framesDecoded', 'framesDropped', 'freezeCount', 'nackCount', 'pliCount', 'firCount'] as const) put(key, video?.[key]);
    put('width', video?.frameWidth); put('height', video?.frameHeight);
    put('jitterMs', video?.jitter * 1000); put('rttMs', pair?.currentRoundTripTime * 1000);
    put('freezeDurationMs', video?.totalFreezesDuration * 1000); put('iceErrorCode', iceErrorCode);
    const prev = this.previous;
    if (video && prev && prev['id'] === video.id && video.timestamp > prev['timestamp']) {
      const delta = (key: string) => typeof video[key] === 'number' && typeof prev[key] === 'number' && video[key] >= prev[key] ? video[key] - prev[key] : NaN;
      const elapsed = video.timestamp - prev['timestamp'];
      put('bitrateKbps', delta('bytesReceived') * 8 / elapsed);
      put('decodeFps', delta('framesDecoded') * 1000 / elapsed);
      put('decodeMs', delta('totalDecodeTime') * 1000 / delta('framesDecoded'));
      put('jitterBufferMs', delta('jitterBufferDelay') * 1000 / delta('jitterBufferEmittedCount'));
    }
    this.previous = video ? { ...video } : undefined;
    return { sequence: ++this.sequence, sampledAtMs: now, state, metrics };
  }
}
